import type { CSSProperties } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { DropzoneInputProps, DropzoneRootProps } from 'react-dropzone'
import { blockTheme, cn, hsl } from '@teach/ui'
import { UploadProgressBody } from './UploadProgressBody'

interface Props {
  icon: LucideIcon
  label: string
  subtitle: string
  rootProps: DropzoneRootProps
  inputProps: DropzoneInputProps
  disabled: boolean
  uploading: boolean
  /** Показываемый процент (`useSmoothProgress`). */
  progress: number | null
}

/**
 * Зона загрузки картинки НА ЭКРАНЕ урока (составной блок): светлая, в цветах
 * `blockTheme` — тёмный `Dropzone` приложения на светлом холсте читался как дыра.
 * Наведение и перетаскивание подсвечивают её акцентом.
 */
export const CanvasDropzone = ({
  icon: Icon,
  label,
  subtitle,
  rootProps,
  inputProps,
  disabled,
  uploading,
  progress,
}: Props) => (
  <div
    {...rootProps}
    className={cn(
      'group flex w-full flex-col items-center justify-center gap-2.5 rounded-2xl outline-none border-2 border-dashed border-[var(--dz-border)] bg-[var(--dz-bg)] px-4 py-7 text-center transition-colors hover:border-[var(--dz-border-hover)] hover:bg-[var(--dz-bg-hover)]',
      disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
    )}
    style={
      {
        '--dz-bg': hsl(blockTheme.primary, 0.06),
        '--dz-bg-hover': hsl(blockTheme.primary, 0.12),
        '--dz-border': hsl(blockTheme.primary, 0.3),
        '--dz-border-hover': hsl(blockTheme.primary, 0.6),
      } as CSSProperties
    }
  >
    <input {...inputProps} />
    {uploading ? (
      <UploadProgressBody
        percent={progress ?? 0}
        colors={{
          title: hsl(blockTheme.foreground),
          track: hsl(blockTheme.primary, 0.15),
          fill: hsl(blockTheme.primary),
        }}
      />
    ) : (
      <>
        {/* Фиолетовая иконка без подложки; на наведении чуть растёт, а стрелка
            (первые два контура lucide-иконки) приподнимается. */}
        <Icon
          className="size-9 transition-transform duration-200 group-hover:scale-110 [&_path]:transition-transform [&_path]:duration-200 group-hover:[&_path:nth-child(-n+2)]:-translate-y-[2px]"
          style={{ color: hsl(blockTheme.primary) }}
          strokeWidth={1.75}
        />
        <span className="text-sm font-semibold" style={{ color: hsl(blockTheme.foreground) }}>
          {label}
        </span>
        <span className="text-xs" style={{ color: hsl(blockTheme.foreground, 0.6) }}>
          {subtitle}
        </span>
      </>
    )}
  </div>
)
